import TitleBar from '../../../components/TitleBar';
import React, { useState } from 'react';
import { Footer } from '../../../components/common/Footer';
import { AntModal, Button, Loading } from '../../../components';
import { MitigatorForm } from '../../dataInputs/mitigating-locations/forms';
import { CREATE_MITIGATION_MATRIX, FETCH_MITIGATORS } from '../queries';
import { useMutation, useQuery } from '@apollo/client';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useUserContext } from '../../../context/PermissionsContext';
import QueryErrorBoundary from '../../../components/error-handling';
import { message } from 'antd';
import TitleUpdater from '../../../components/common/TitleUpdater';

type Mitigator = {
  contactName: string;
  email: string;
  phone: string;
  vendorName: string;
  uuid: string;
};

type SelectedVendor = {
  assignedMitigators: Mitigator[];
  unAssignedMitigators: Mitigator[];
};

interface DataReturned {
  selectedVendors: SelectedVendor;
}

function AutomotiveDivisionLocations() {
  const navigate = useNavigate();
  const params = useParams();
  let [searchParams] = useSearchParams();
  const divisionName = searchParams.get('division')!;
  const locationId = searchParams.get('location')!;
  const locationName = searchParams.get('locationName')!;
  const level = parseInt(searchParams.get('level')!);
  const divisionId = params.divisionId!;
  const [modal2Open, setModal2Open] = useState(false);

  const { isAdmin, canEdit } = useUserContext();

  const { data, loading, error } = useQuery<DataReturned>(FETCH_MITIGATORS, {
    variables: {
      location: { uuid: locationId },
      division: {
        uuid: divisionId,
      },
    },
  });

  const [CreateMitigationMatrix] = useMutation(CREATE_MITIGATION_MATRIX, {
    refetchQueries: [
      'GetMapping',
      'DivisionSelectionDashboard',
      'fetchLevelLocations',
    ],
  });

  const { handleSubmit, register } = useForm();

  const canUpdate = isAdmin || canEdit;

  const handleCancel = () => {
    setModal2Open(false);
  };

  const onFinish: SubmitHandler<any> = async (values: {
    vendors: string[];
  }) => {
    if (!canUpdate) return;

    if (!values.vendors || values.vendors.length === 0) {
      message.error('Please select at least one location/vendor');
      return;
    }

    let response;

    const vendorsList = Array.isArray(values.vendors)
      ? values.vendors
      : [values.vendors];

    const vendors = vendorsList.map((vendor: string) => {
      return { uuid: vendor };
    });

    try {
      response = await CreateMitigationMatrix({
        variables: {
          data: {
            division: {
              uuid: divisionId,
            },
            location: {
              uuid: locationId,
            },
            vendors: vendors,
          },
        },
      });
      if (response.data) {
        message.success('Changes saved');
        navigate(
          `/${divisionId}/external-mitigation?division=${divisionName}&location=${locationId}&locationName=${locationName}&level=${level}`,
        );
      }
    } catch (e: any) {
      console.log(e);
      message.error('An error occurred. Please try again.');
    }
  };

  return (
    <div>
      <TitleUpdater title={`External Mitigation Selection`} />
      <TitleBar
        title="External Mitigation Options"
        subtitle={`${divisionName} // ${locationName}`}
        brief={`From the list of external (non-owned) mitigating options below, select which can support
                      the production of products at the ${locationName.toUpperCase()} location. If a location/vendor is missing, use the “Add
                      External Option” button to add them.`}
      />
      <QueryErrorBoundary error={error}>
        <>
          <div className="w-full p-16">
            <form onSubmit={handleSubmit(onFinish)} className="w-full">
              <div className="w-full flex flex-row flex-wrap justify-start">
                {data?.selectedVendors.assignedMitigators.map((mitigator) => (
                  <div
                    key={mitigator.uuid}
                    className={`w-100 border m-2 shadow-button border-secondary-100 bg-white rounded-lg p-4 hover:bg-primary-200/10`}
                  >
                    <label>
                      <input
                        type="checkbox"
                        value={mitigator.uuid}
                        disabled
                        checked
                        className="w-8 h-8 cursor-pointer rounded-full border-tertiary-100 hover:border-checked/50 bg-tertiary-200 text-checked hover:bg-checked/50 focus:ring-checked"
                        {...register('vendors', {})}
                      />
                      <span className="mx-4   tracking-widest text-black text-sm">
                        {mitigator.vendorName}
                      </span>
                    </label>
                  </div>
                ))}
                {!loading ? (
                  data?.selectedVendors.unAssignedMitigators.map(
                    (mitigator) => (
                      <div
                        key={mitigator.uuid}
                        className={`w-100 border m-2 shadow-button border-secondary-100 bg-white rounded-lg p-4 hover:bg-primary-200/10`}
                      >
                        <label>
                          <input
                            type="checkbox"
                            value={mitigator.uuid}
                            className="w-8 h-8 cursor-pointer rounded-full border-tertiary-100 hover:border-checked/50 bg-tertiary-200 text-checked hover:bg-checked/50 focus:ring-checked"
                            {...register('vendors', {})}
                          />
                          <span className="mx-4   tracking-widest text-black text-sm">
                            {mitigator.vendorName}
                          </span>
                        </label>
                      </div>
                    ),
                  )
                ) : (
                  <Loading />
                )}
              </div>
              <div className="w-full flex flex-col flex-wrap items-center mt-6">
                <Button
                  type="primary"
                  className="text-sm min-w-[180px]"
                  htmlType={'submit'}
                  disabled={!canUpdate}
                >
                  Save
                </Button>
              </div>
            </form>
          </div>
          <div className="flex flex-col flex-nowrap justify-center items-center mb-20">
            <Button
              type="primary"
              className="w-full my-8 max-w-150"
              onClick={() => setModal2Open(true)}
              disabled={!canUpdate}
            >
              + Add External Option
            </Button>
          </div>
        </>
      </QueryErrorBoundary>
      <Footer
        backPath={`/${divisionId}/internal-mitigation?division=${divisionName}&location=${locationId}&locationName=${locationName}&level=${level}`}
        nextPath={`/${divisionId}/external-mitigation?division=${divisionName}&location=${locationId}&locationName=${locationName}&level=${level}`}
      />
      <AntModal
        title="Add External Option"
        openModal={modal2Open}
        onCancel={handleCancel}
      >
        <MitigatorForm onCancel={handleCancel} edit={false} />
      </AntModal>
    </div>
  );
}

export default AutomotiveDivisionLocations;
